import React, { useState } from "react";
import { Button } from "../../components/template";

/**
 * Scoring:
 * Correct / most balanced answer = 1 point
 * Other options = 0 points
 * Max Score = 15
 */

// Index of correct option for each question
const correctAnswers = [
  1, // Q1
  0, // Q2
  0, // Q3
  0, // Q4
  0, // Q5
  0, // Q6
  0, // Q7
  0, // Q8
  0, // Q9
  0, // Q10
  0, // Q11
  0, // Q12
  0, // Q13
  0, // Q14
  0, // Q15
];

const quizData = [
  {
    question: "Which meal is generally recommended to be the heaviest of the day?",
    options: ["Breakfast", "Lunch", "Dinner"],
  },
  {
    question: "Regularly eating very heavy dinners is most likely to affect…",
    options: [
      "Digestion and sleep quality",
      "Only body weight",
      "Morning energy levels only",
    ],
  },
  {
    question: "Skipping meals frequently can eventually disturb…",
    options: [
      "Hunger and digestion signals",
      "Metabolism positively",
      "Fat loss efficiency",
    ],
  },
  {
    question: "Eating meals at very irregular times usually leads to…",
    options: [
      "Confused digestive rhythm",
      "Better flexibility in digestion",
      "No noticeable impact",
    ],
  },
  {
    question: "Which description best matches true physical hunger?",
    options: [
      "Gradual and steady",
      "Sudden and urgent",
      "Emotion-driven",
    ],
  },
  {
    question: "Cravings are most commonly influenced by…",
    options: [
      "Stress, sleep, and food quality",
      "Lack of discipline",
      "The body needing sugar",
    ],
  },
  {
    question: "Warm, freshly cooked food is often considered easier to digest because…",
    options: [
      "It supports digestive activity",
      "It has fewer calories",
      "It automatically reduces portions",
    ],
  },
  {
    question: "Regular consumption of very cold food or drinks may…",
    options: [
      "Slow digestion for some people",
      "Improve digestion",
      "Have no effect at all",
    ],
  },
  {
    question: "Leftover food that is reheated multiple times is generally thought to…",
    options: [
      "Lose freshness and digestibility",
      "Become more nutritious",
      "Be equal to freshly cooked food",
    ],
  },
  {
    question: "Fruits are generally easier to digest when eaten…",
    options: [
      "Alone or with light meals",
      "Right after heavy meals",
      "Late at night",
    ],
  },
  {
    question: "Eating very quickly mainly affects…",
    options: [
      "Digestion and fullness cues",
      "Only calorie intake",
      "Nutrient absorption positively",
    ],
  },
  {
    question: "Drinking small amounts of water during meals is…",
    options: [
      "Usually fine for most people",
      "Always harmful",
      "Necessary in large quantities",
    ],
  },
  {
    question: "Which statement about fats and oils is most accurate?",
    options: [
      "Both quality and quantity matter",
      "All fats are unhealthy",
      "Healthy fats can be eaten freely",
    ],
  },
  {
    question: "The idea that “oil-free food is always healthier” is…",
    options: [
      "An oversimplification",
      "Completely true",
      "Proven by Ayurveda",
    ],
  },
  {
    question: "A balanced understanding of healthy eating includes…",
    options: [
      "What, when, and how we eat",
      "Calories alone",
      "Following strict food rules",
    ],
  },
];

/* -------------------- INTRO PAGE -------------------- */

const IntroPage = ({ onStart }) => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10 text-center">
    <div className="max-w-lg bg-white p-6 rounded-2xl shadow-md">
      <h1 className="text-2xl font-bold text-primary mb-4">
        Nutrition & Healthy Eating – Knowledge Self-Assessment
      </h1>

      <p className="text-gray-700">
        Choose the option that feels most accurate based on your understanding
        of healthy eating, not necessarily what you practise daily.
      </p>

      <Button
        variant="primary"
        size="md"
        className="mt-6"
        text="Start Quiz"
        onClick={onStart}
      />
    </div>
  </div>
);

/* -------------------- QUIZ PAGE -------------------- */

const QuizPage = ({ onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(Array(quizData.length).fill(null));
  const [error, setError] = useState("");

  const handleSelect = (index) => {
    const updated = [...answers];
    updated[current] = index;
    setAnswers(updated);
    setError("");
  };

  const handleNext = () => {
    if (answers[current] === null) {
      setError("Please select an option to proceed.");
      return;
    }

    if (current < quizData.length - 1) {
      setCurrent(current + 1);
    } else {
      onComplete(answers);
    }
  };

  const handlePrev = () => {
    setError("");
    if (current > 0) setCurrent(current - 1);
  };

  const progress = ((current + 1) / quizData.length) * 100;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10">
      <div className="w-full max-w-2xl bg-white p-8 rounded-2xl shadow-lg text-center">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Question {current + 1} of {quizData.length}
          </p>
          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <p className="text-[#578d48] font-bold text-xl mb-4">
          {quizData[current].question}
        </p>

        <div className="space-y-3">
          {quizData[current].options.map((opt, i) => (
            <label
              key={i}
              className={`flex items-center justify-center p-3 rounded-lg cursor-pointer transition
                ${
                  answers[current] === i
                    ? "bg-primary text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
            >
              <input
                type="radio"
                checked={answers[current] === i}
                onChange={() => handleSelect(i)}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>

        {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
      </div>

      <div className="flex mt-6 space-x-4">
        {current > 0 && (
          <Button text="Previous" variant="whitish" onClick={handlePrev} />
        )}
        <Button
          text={current === quizData.length - 1 ? "Submit" : "Next"}
          variant="primary"
          onClick={handleNext}
        />
      </div>
    </div>
  );
};

/* -------------------- RESULT PAGE -------------------- */

const ResultPage = ({ answers, onRetake }) => {
  const totalScore = answers.reduce(
    (sum, ans, i) => sum + (ans === correctAnswers[i] ? 1 : 0),
    0
  );

  let resultText = "";

  if (totalScore >= 12) {
    resultText = `🥗 : Strong Nutrition Awareness
You understand healthy eating as a balance of digestion, timing, food quality, and habits.
You can identify common myths and extremes.
Focus: consistency over perfection.`;
  } else if (totalScore >= 7) {
    resultText = `🌱 : Partial Awareness (Very Common)
You know several principles but may still carry some mixed or outdated beliefs.
This is where most people are.
Focus: digestion-first thinking and simplicity.`;
  } else {
    resultText = `🍃 : Nutrition Knowledge Needs Strengthening
You may be influenced by trends, extremes, or conflicting advice.
This is a starting point, not a failure.
Focus: building foundations before advanced strategies.`;
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10 text-center">
      <div className="max-w-lg bg-white p-6 rounded-2xl shadow-md">
        <h2 className="text-2xl font-bold text-secondary mb-4">
          Your Nutrition Knowledge Score
        </h2>

        <p className="text-lg font-semibold text-primary">
          Total Score: {totalScore} / 15
        </p>

        <pre className="whitespace-pre-wrap text-left text-gray-700 mt-4">
          {resultText}
        </pre>

        <Button
          variant="primary"
          size="md"
          className="mt-6"
          text="Retake Quiz"
          onClick={onRetake}
        />
      </div>
    </div>
  );
};

/* -------------------- MAIN COMPONENT -------------------- */

const NutritionKnowledgeQuiz = () => {
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState(null);

  return (
    <>
      {!started ? (
        <IntroPage onStart={() => setStarted(true)} />
      ) : completed ? (
        <ResultPage
          answers={answers}
          onRetake={() => {
            setStarted(false);
            setCompleted(false);
            setAnswers(null);
          }}
        />
      ) : (
        <QuizPage
          onComplete={(ans) => {
            setAnswers(ans);
            setCompleted(true);
          }}
        />
      )}
    </>
  );
};

export default NutritionKnowledgeQuiz;
